import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

/**
 * Public whole-material publication from the V2 author workspace.
 * Reuses V1's existing authenticated /preview and /publish snapshot contract.
 * Never routes paid Book spreads through a whole-material publication.
 */
const PUBLIC_DESTINATIONS = new Set(["open-library", "open-library-author-intro"]);

export function isPublicWholeMaterialDestination(volume) {
  return Boolean(volume && !volume.product_id && PUBLIC_DESTINATIONS.has(volume.slug));
}

export function EditorialWholeMaterialPublishV2({ material, volume, editorOpen = false, onPublished }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("review"); // review | ready | success
  const [review, setReview] = useState(null);
  const [error, setError] = useState("");
  const [published, setPublished] = useState(null);

  useEffect(() => {
    setOpen(false);
    setReview(null);
    setPhase("review");
    setError("");
    setPublished(null);
  }, [material?.id, volume?.id]);

  if (!material || !isPublicWholeMaterialDestination(volume) || material.status === "archived") return null;

  const materialId = material.id;
  const isAuthorIntroduction = volume.slug === "open-library-author-intro";
  const previewPath = `/admin/editorial-v2/materials/${encodeURIComponent(materialId)}/preview-in-library`;
  const close = () => { if (!busy) { setOpen(false); setReview(null); setPhase("review"); setError(""); } };

  const start = () => {
    if (editorOpen || busy) return;
    setOpen(true);
    setPhase("review");
    setReview(null);
    setError("");
    setPublished(null);
  };

  const checkCurrentDraft = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setReview(null);
    try {
      // This existing backend GET marks previewed_revision = draft_revision.
      // No public content is released at this step.
      const { data } = await api.get(`/admin/editorial/materials/${encodeURIComponent(materialId)}/preview`);
      if (data?.mode !== "draft" || data?.material?.id !== materialId
        || data?.material?.volume_id !== volume.id) {
        throw new Error("Место публикации изменилось. Закройте окно и обновите Редакцию.");
      }
      const visible = (data.blocks || []).filter((b) => b?.is_visible !== false);
      if (!visible.length) throw new Error("Нет сохранённых разворотов для публикации.");
      if (!Number.isInteger(data.material.draft_revision)
        || data.material.previewed_revision !== data.material.draft_revision) {
        throw new Error("Не удалось подтвердить актуальную версию материала.");
      }
      setReview({
        revision: data.material.draft_revision,
        title: data.material.title,
        count: visible.length,
        masterTypes: visible.map((b) => b.master_type),
      });
      setPhase("ready");
    } catch (e) {
      setError(formatApiError(e));
      setPhase("review");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    if (busy || phase !== "ready" || !review) return;
    setBusy(true);
    setError("");
    let committed = false;
    try {
      // The backend rejects stale revisions and materials using per-spread states.
      const { data } = await api.post(`/admin/editorial/materials/${encodeURIComponent(materialId)}/publish`);
      if (data?.id !== materialId || data?.status !== "published" || !data.published_snapshot_id) {
        throw new Error("Сервер не подтвердил публикацию. Проверьте статус материала.");
      }
      committed = true;
      onPublished?.(data);
      // Read the frozen release to make the success state verifiable.
      const result = await api.get(`/admin/editorial/materials/${encodeURIComponent(materialId)}/published`);
      if (result.data?.mode !== "published" || !(result.data.blocks || []).length) {
        throw new Error("Публикация создана, но не удалось проверить снимок. Обновите Редакцию.");
      }
      setPublished({ title: data.title, count: result.data.blocks.length });
      setPhase("success");
    } catch (e) {
      setError(committed
        ? "Публикация сохранена, но её проверка не завершилась. Обновите страницу перед повторным действием."
        : formatApiError(e));
      if (committed) setPhase("success");
      else { setReview(null); setPhase("review"); }
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={start} disabled={editorOpen || busy}
        className="rounded-xl bg-espresso px-4 py-3 text-xs text-cream disabled:opacity-50"
        title={editorOpen ? "Сначала сохраните и закройте редактируемый разворот" : "Публикация целого материала"}
        data-testid="editorial-v2-whole-material-publish-open">
        {material.status === "published" ? "Обновить публикацию" : "Опубликовать материал"}
      </button>

      {open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 px-4 py-5"
          data-testid="editorial-v2-whole-material-publish-dialog">
          <div role="dialog" aria-modal="true" aria-labelledby="editorial-v2-whole-publish-title"
            className="w-full max-w-xl overflow-y-auto rounded-3xl bg-[#FAF5EC] p-6 text-espresso shadow-xl"
            style={{ maxHeight: "90vh" }}>
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Открытая библиотека · публикация целиком</div>
            <h2 id="editorial-v2-whole-publish-title" className="mt-2 font-serif text-2xl">
              {phase === "success" ? "Публикация сохранена" : "Публикация материала"}
            </h2>
            <p className="mt-3 font-serif text-lg">{review?.title || material.title}</p>
            <p className="mt-2 text-sm text-espresso/70">
              {phase === "success"
                ? "Опубликованная версия отделена от рабочего черновика."
                : "Все видимые сохранённые развороты будут опубликованы одной версией. Материал станет доступен читателям Открытой библиотеки."}
            </p>
            {phase === "review" && (
              <div className="mt-5 space-y-3 rounded-xl border border-espresso/15 bg-white/65 p-4">
                {isAuthorIntroduction ? (
                  <Link to={previewPath} target="_blank" rel="noopener noreferrer"
                    className="block text-sm font-medium underline underline-offset-4"
                    data-testid="editorial-v2-whole-preview-link">
                    Посмотреть всю статью на странице библиотеки
                  </Link>
                ) : (
                  <p className="text-sm">Сначала проверьте содержание всех разворотов в Редакции.</p>
                )}
                <p className="text-xs text-espresso/70">После просмотра подтвердите актуальную сохранённую версию. Это ещё не публикация.</p>
                <button type="button" onClick={checkCurrentDraft} disabled={busy}
                  className="rounded-xl border border-espresso/30 bg-white px-4 py-2 text-sm disabled:opacity-50"
                  data-testid="editorial-v2-whole-check">
                  {busy ? "Проверяем…" : "Я проверила — подготовить к публикации"}
                </button>
              </div>
            )}
            {phase === "ready" && review && (
              <div className="mt-5 rounded-xl border border-espresso/15 bg-white/65 p-4">
                <p className="text-sm">Проверена сохранённая версия № {review.revision}. Видимых разворотов: {review.count}.</p>
                <p className="mt-2 text-xs text-espresso/65">Подтверждение выпустит материал для читателей. При изменении черновика сервер отменит устаревшее подтверждение.</p>
                <button type="button" onClick={publish} disabled={busy}
                  className="mt-4 rounded-xl bg-espresso px-4 py-3 text-sm text-cream disabled:opacity-50"
                  data-testid="editorial-v2-whole-confirm">
                  {busy ? "Публикуем…" : "Подтвердить и опубликовать целиком"}
                </button>
              </div>
            )}
            {phase === "success" && (
              <div className="mt-5 rounded-xl border border-espresso/15 bg-white/65 p-4"
                data-testid="editorial-v2-whole-publish-success">
                <p className="text-sm">{published
                  ? `Подтверждена опубликованная версия, разворотов: ${published.count}.`
                  : "Сохранение подтверждено. Обновите Редакцию для проверки статуса."}</p>
                {published && <Link to="/library" className="mt-3 inline-block text-sm underline underline-offset-4">
                  Открыть Открытую библиотеку
                </Link>}
              </div>
            )}
            {error && <p role="alert" className="mt-4 text-sm text-red-800">{error}</p>}
            <div className="mt-6 flex justify-end">
              <button type="button" disabled={busy} onClick={close}
                className="rounded-xl border border-espresso/25 px-4 py-2 text-sm disabled:opacity-50"
                data-testid="editorial-v2-whole-close">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
